/**
 * Запросы, которые монитор выполняет на локальной базе кассы.
 *
 * Особенность: запрос уходит текстом и выполняется на ЧУЖОЙ базе, схема которой
 * зависит от версии кассы. Касса накатывает свои миграции сама при старте, и на
 * версии 2023 года нет половины колонок, которые есть сегодня. Обычный запрос
 * со «всеми нужными» колонками там просто упадёт, а старая касса про ошибку
 * даже не сообщит — монитор упрётся в таймаут и покажет пустоту.
 *
 * Поэтому при подключении сначала читается фактическая схема
 * (information_schema), а дальше каждый SELECT собирается из тех колонок,
 * которые на этой кассе реально есть.
 */

/** Таблицы, чью схему тянем при подключении. Всю базу читать незачем. */
const SCHEMA_TABLES = [
	'cheques',
	'cheques_products',
	'cheques_payments',
	'cheques_statuses',
	'cheques_logs',
	'cheques_courier_status',
	'cheques_discounts',
	'clients',
	'clients_addresses',
	'halls',
	'tables',
	'users',
	'products',
	'payment_types',
	'shifts',
]

/** Строковый литерал для SQL: кавычка внутри удваивается. */
export function sqlString(value) {
	return `'${String(value ?? '').replace(/'/g, "''")}'`
}

export function sqlInt(value) {
	const number = parseInt(value, 10)
	return Number.isFinite(number) ? number : 0
}

export function schemaQuery() {
	const list = SCHEMA_TABLES.map(sqlString).join(', ')

	return `SELECT table_name, column_name
		FROM information_schema.columns
		WHERE table_schema = 'public'
		AND table_name IN (${list})
		ORDER BY table_name, ordinal_position`
}

/** Строки information_schema → { table: [column, ...] } */
export function buildSchema(rows) {
	const schema = {}

	for (const row of rows || []) {
		const table = row.table_name
		if (!table) continue
		if (!schema[table]) schema[table] = []
		schema[table].push(row.column_name)
	}

	return schema
}

export function hasTable(schema, table) {
	return Array.isArray(schema?.[table]) && schema[table].length > 0
}

export function hasColumn(schema, table, column) {
	return !!schema?.[table]?.includes(column)
}

/** Оставить из списка только те колонки, что есть на этой кассе. */
function pick(schema, table, alias, columns) {
	return columns
		.filter((column) => hasColumn(schema, table, column))
		.map((column) => `${alias}.${column}`)
}

/**
 * COALESCE только по существующим колонкам; если нет ни одной — NULL.
 * Так «новое поле с откатом на старое» не роняет запрос на старой кассе.
 */
function coalesce(parts, alias) {
	const usable = parts.filter(Boolean)

	if (usable.length === 0) return `NULL AS ${alias}`
	if (usable.length === 1) return `${usable[0]} AS ${alias}`

	return `COALESCE(${usable.join(', ')}) AS ${alias}`
}

function column(schema, table, alias, name) {
	return hasColumn(schema, table, name) ? `${alias}.${name}` : null
}

/**
 * Список чеков кассы — тот же набор, что видит кассир на экране открытых чеков
 * (chequeController.index: всё, кроме status 10 и -1).
 */
export function chequesQuery(schema, options = {}) {
	const {
		limit = 50,
		onlyOpen = true,
		search = '',
		orderTypeId = null,
	} = options

	const columns = pick(schema, 'cheques', 'ch', [
		'id',
		'pos_id',
		'shift_id',
		'cheque_number',
		'status',
		'kitchen_status',
		'synchronized',
		'cloud_id',
		'total_amount',
		'total_amount_service',
		'paid',
		'discount',
		'service_percentage',
		'delivery_price',
		'surcharge',
		'people_number',
		'hall_id',
		'table_id',
		'client_id',
		'courier_id',
		'payment_status',
		'monitor_status',
		'order_type',
		'order_type_delivery',
		'comment',
		'note',
		'cheque_printed',
		'transaction_id',
		'operator_name',
		'device',
		'desktop_client_version',
		'external_id',
		'external_name',
		'external_prefix',
		'external_info',
		'external_courier_info',
		'promo_code',
		'cashier_login',
		'waiter_login',
		'payment_type_id',
		'navigate',
		'eating_kit',
		'cheque_opened_at',
		'cheque_changed_at',
		'cheque_closed_at',
	])

	columns.push(coalesce([
		column(schema, 'cheques', 'ch', 'order_type_id'),
		column(schema, 'cheques', 'ch', 'order_type'),
	], 'order_type_resolved'))

	columns.push(coalesce([
		hasTable(schema, 'halls') ? 'h.name' : null,
		column(schema, 'cheques', 'ch', 'hall_name'),
	], 'hall_name'))

	columns.push(coalesce([
		hasTable(schema, 'tables') ? 't.name' : null,
		column(schema, 'cheques', 'ch', 'table_name'),
	], 'table_name'))

	if (hasTable(schema, 'clients')) {
		if (hasColumn(schema, 'clients', 'first_name')) columns.push('c.first_name AS client_name')
		if (hasColumn(schema, 'clients', 'phone1')) columns.push('c.phone1 AS client_phone')
	}

	// Имя человека берём, только если колонка есть: запрос выполняется на чужой
	// базе, и одна отсутствующая колонка роняет выборку целиком.
	const hasUsers = hasTable(schema, 'users') && hasColumn(schema, 'users', 'first_name')

	if (hasUsers && hasColumn(schema, 'cheques', 'courier_id')) {
		columns.push('courier.first_name AS courier_name')
	}

	const joins = []
	if (hasTable(schema, 'halls')) joins.push('LEFT JOIN halls h ON h.id = ch.hall_id')
	if (hasTable(schema, 'tables')) joins.push('LEFT JOIN tables t ON t.id = ch.table_id')
	if (hasTable(schema, 'clients')) joins.push('LEFT JOIN clients c ON c.id = ch.client_id')
	if (hasUsers && hasColumn(schema, 'cheques', 'courier_id')) {
		joins.push('LEFT JOIN users courier ON courier.id = ch.courier_id')
	}

	// Имя сотрудника, а не логин. Ссылка на пользователя двойная: в старых
	// чеках лежит только логин, в новых — ещё и id, поэтому касса джойнит
	// users дважды и берёт первое непустое. Повторяем тот же приём.
	for (const role of ['waiter', 'cashier']) {
		if (!hasUsers) continue

		const hasId = hasColumn(schema, 'cheques', `${role}_id`)
		const hasLogin = hasColumn(schema, 'cheques', `${role}_login`)

		if (hasId) {
			joins.push(`LEFT JOIN users ${role}_by_id ON ${role}_by_id.id = ch.${role}_id`)
		}
		if (hasLogin) {
			const guard = hasId ? `ch.${role}_id IS NULL AND ` : ''
			joins.push(`LEFT JOIN users ${role}_by_login ON ${guard}${role}_by_login.login = ch.${role}_login`)
		}

		columns.push(coalesce([
			hasId ? `${role}_by_id.first_name` : null,
			hasLogin ? `${role}_by_login.first_name` : null,
		], `${role}_name`))
	}

	// Адрес доставки лежит отдельной таблицей, в чеке только ссылка.
	if (hasTable(schema, 'clients_addresses') && hasColumn(schema, 'cheques', 'address_id')) {
		joins.push('LEFT JOIN clients_addresses ca ON ca.id = ch.address_id')
		columns.push('ca.name AS client_address')
		columns.push(...pick(schema, 'clients_addresses', 'ca', [
			'apartment',
			'floor',
			'entrance',
			'intercom',
		]))
	}

	if (hasTable(schema, 'payment_types') && hasColumn(schema, 'cheques', 'payment_type_id')) {
		joins.push('LEFT JOIN payment_types pt ON pt.payment_type_id = ch.payment_type_id')
		columns.push('pt.name AS payment_type_name')
	}

	const where = ['1 = 1']

	// Открытые чеки в кассе — это «всё, кроме закрытых (10) и удалённых (-1)».
	if (onlyOpen) where.push('ch.status <> 10 AND ch.status <> -1')

	if (orderTypeId) {
		const orderType = hasColumn(schema, 'cheques', 'order_type_id')
			? 'COALESCE(ch.order_type_id, ch.order_type)'
			: 'ch.order_type'
		where.push(`${orderType} = ${sqlInt(orderTypeId)}`)
	}

	const term = String(search || '').trim()
	if (term) {
		const like = sqlString(`%${term}%`)
		const conditions = []

		conditions.push(`CAST(ch.id AS TEXT) = ${sqlString(term)}`)
		if (hasColumn(schema, 'cheques', 'cheque_number')) {
			conditions.push(`CAST(ch.cheque_number AS TEXT) = ${sqlString(term)}`)
		}
		if (hasColumn(schema, 'cheques', 'cloud_id')) {
			conditions.push(`CAST(ch.cloud_id AS TEXT) = ${sqlString(term)}`)
		}
		// Ради чего всё и затевалось: заказ агрегатора ищется по его номеру.
		if (hasColumn(schema, 'cheques', 'external_id')) {
			conditions.push(`ch.external_id ILIKE ${like}`)
		}
		if (hasColumn(schema, 'cheques', 'transaction_id')) {
			conditions.push(`ch.transaction_id ILIKE ${like}`)
		}
		if (hasTable(schema, 'clients') && hasColumn(schema, 'clients', 'phone1')) {
			conditions.push(`c.phone1 ILIKE ${like}`)
		}
		if (hasTable(schema, 'clients')) {
			conditions.push(`c.first_name ILIKE ${like}`)
		}

		where.push(`(${conditions.join(' OR ')})`)
	}

	return `SELECT
			${columns.join(',\n\t\t\t')}
		FROM cheques ch
		${joins.join('\n\t\t')}
		WHERE ${where.join('\n\t\tAND ')}
		ORDER BY ch.id DESC
		LIMIT ${sqlInt(limit)}`
}

export function chequeProductsQuery(schema, chequeId) {
	const columns = pick(schema, 'cheques_products', 'chp', [
		'id',
		'cheque_id',
		'product_id',
		'quantity',
		'price',
		'sale_price',
		'total_price',
		'status',
		'kitchen_status',
		'comment',
		'service_percentage',
		'product_system_type_id',
		'created_at',
	])

	if (hasTable(schema, 'products')) columns.push('p.name AS product_name')

	const join = hasTable(schema, 'products')
		? 'LEFT JOIN products p ON p.id = chp.product_id'
		: ''

	return `SELECT
			${columns.join(',\n\t\t\t')}
		FROM cheques_products chp
		${join}
		WHERE chp.cheque_id = ${sqlInt(chequeId)}
		ORDER BY chp.id ASC
		LIMIT 300`
}

export function chequePaymentsQuery(schema, chequeId) {
	const columns = pick(schema, 'cheques_payments', 'chp', [
		'id',
		'cheque_id',
		'amount',
		'transaction_type',
		'payment_type_id',
		'payment_purpose_id',
		'payment_type_category_id',
		'created_at',
	])

	if (columns.length === 0) return null

	let join = ''
	if (hasTable(schema, 'payment_types') && hasColumn(schema, 'cheques_payments', 'payment_type_id')) {
		join = 'LEFT JOIN payment_types pt ON pt.payment_type_id = chp.payment_type_id'
		columns.push('pt.name AS payment_type_name')
	}

	return `SELECT
			${columns.join(',\n\t\t\t')}
		FROM cheques_payments chp
		${join}
		WHERE chp.cheque_id = ${sqlInt(chequeId)}
		ORDER BY chp.id ASC
		LIMIT 100`
}

/**
 * История статусов чека — по ней видно, что именно касса приняла от облака:
 * строку сюда пишет обработчик sync.cheque.status.
 *
 * Связь идёт по cloud_id (id чека в облаке), а не по локальному id: в таблице
 * своей ссылки на cheques нет, id строки — тоже облачный. Поэтому у чека без
 * cloud_id истории тут не будет вовсе, и это не ошибка.
 */
export function chequeStatusesQuery(schema, cloudId) {
	if (!hasTable(schema, 'cheques_statuses')) return null
	if (!cloudId) return null

	const columns = pick(schema, 'cheques_statuses', 'chs', [
		'id',
		'cloud_id',
		'status',
		'user_login',
		'user_role',
		'created_at',
	])

	return `SELECT
			${columns.join(',\n\t\t\t')}
		FROM cheques_statuses chs
		WHERE chs.cloud_id = ${sqlInt(cloudId)}
		ORDER BY chs.created_at ASC, chs.id ASC
		LIMIT 100`
}

/**
 * История действий по чеку: кто открыл, кто печатал, кто добавлял позиции.
 *
 * Разбор params в текст лежит в helpers/chequeLogMessage — тот же, что у
 * закрытых чеков в вебе и в самой кассе, чтобы события назывались одинаково.
 */
export function chequeLogsQuery(schema, chequeId) {
	if (!hasTable(schema, 'cheques_logs')) return null

	const columns = pick(schema, 'cheques_logs', 'chl', [
		'id',
		'cheque_id',
		'params',
		'created_by',
		'created_at',
	])

	let join = ''
	if (hasTable(schema, 'users') && hasColumn(schema, 'users', 'first_name')) {
		join = 'LEFT JOIN users u ON u.login = chl.created_by'
		columns.push('u.first_name AS created_by_name')
	}

	return `SELECT
			${columns.join(',\n\t\t\t')}
		FROM cheques_logs chl
		${join}
		WHERE chl.cheque_id = ${sqlInt(chequeId)}
		ORDER BY chl.id ASC
		LIMIT 300`
}

/**
 * Позиции, оплаты, статусы и история — одним запросом.
 *
 * Четырьмя отдельными это было бы четыре круга по сокету, а они идут строго по
 * одному (ответ старой кассы нельзя сопоставить с запросом иначе, чем по
 * порядку). При типичном ответе кассы в секунду-три открытие чека занимало бы
 * до десятка секунд. Здесь каждый список сворачивается в json_agg, и круг один.
 */
export function chequeDetailQuery(schema, chequeId, cloudId) {
	const parts = []

	const add = (alias, sql) => {
		// Алиас src, а не row: ROW в postgres — ключевое слово.
		parts.push(sql
			? `(SELECT COALESCE(json_agg(src), '[]'::json) FROM (${sql}) src) AS ${alias}`
			: `'[]'::json AS ${alias}`)
	}

	add('products', chequeProductsQuery(schema, chequeId))
	add('payments', chequePaymentsQuery(schema, chequeId))
	add('statuses', chequeStatusesQuery(schema, cloudId))
	add('logs', chequeLogsQuery(schema, chequeId))

	return `SELECT
			${parts.join(',\n\t\t\t')}`
}

/** Сколько всего чеков и когда последний — дешёвая проверка «касса жива». */
export function chequesSummaryQuery(schema) {
	const opened = hasColumn(schema, 'cheques', 'cheque_opened_at')
		? 'MAX(ch.cheque_opened_at)'
		: 'NULL'
	const changed = hasColumn(schema, 'cheques', 'cheque_changed_at')
		? 'MAX(ch.cheque_changed_at)'
		: 'NULL'

	return `SELECT
			COUNT(*) FILTER (WHERE ch.status <> 10 AND ch.status <> -1) AS opened_count,
			COUNT(*) AS total_count,
			${opened} AS last_opened_at,
			${changed} AS last_changed_at,
			NOW() AS pos_time
		FROM cheques ch`
}

export const CHEQUE_STATUS_NAMES = {
	'-1': 'Удалён',
	'0': 'Не подтверждён',
	'1': 'Открыт',
	'2': 'Готовится',
	'3': 'Готов',
	'10': 'Закрыт',
}

/**
 * Подписи шагов в истории статусов чека.
 *
 * Это отдельный от CHEQUE_STATUS_NAMES словарь: там подписывается текущее
 * состояние чека («Открыт»), здесь — свершившееся действие («Создано»,
 * «Получено»). Набор и формулировки взяты из правой панели кассы
 * (OpenedCheques), чтобы при разговоре с точкой обе стороны читали одно и то же.
 */
export const CHEQUE_STATUS_STEP_NAMES = {
	'-11': 'Черновик',
	'-10': 'Создано',
	'-1': 'Удалено',
	'0': 'Принято',
	'1': 'Подтверждено',
	'2': 'Приготовлено',
	'3': 'Приготовлено',
	'4': 'Приготовлено',
	'7': 'Упаковано',
	'8': 'В пути',
	'9': 'Оплачено',
	'10': 'Закрыто',
}

export const ORDER_TYPE_NAMES = {
	'1': 'Зал',
	'2': 'С собой',
	'3': 'Доставка',
}
